import { Roboto, Roboto_Mono, Space_Grotesk } from 'next/font/google';
import { useRouter } from 'next/router';
import { Component as ReactComponent, useEffect, useRef } from 'react';
import { twJoin } from 'tailwind-merge';

import type { AppProps } from 'next/app';
import type { ErrorInfo, ReactNode } from 'react';

import 'styles/global.css';

const spaceGrotesk = Space_Grotesk({
  subsets: ['latin'],
  weight: ['300', '400', '500', '700'],
  display: 'optional',
  variable: '--font-space-grotesk',
});

const roboto = Roboto({
  subsets: ['latin'],
  weight: ['400', '500', '700'],
  display: 'optional',
  variable: '--font-roboto',
});

const robotoMono = Roboto_Mono({
  subsets: ['latin'],
  weight: ['300', '500', '700'],
  display: 'optional',
  variable: '--font-roboto-mono',
});

// Match the normalization used by src/scripts/getRedirects.js so the path the
// router hands us in routeChangeStart lines up with the map keys.
const toRedirectKey = (url: string) => {
  const pathOnly = url.split('?')[0].split('#')[0];
  const hasExtension = /\.[a-z0-9]+$/i.test(pathOnly);

  if (!hasExtension && pathOnly && !pathOnly.endsWith('/')) {
    return `${pathOnly}/`;
  }

  return pathOnly;
};

// Builder-managed redirects live in next.config.js redirects() and work for
// direct navigation, but the site sits behind CloudFront, which strips the
// x-nextjs-redirect header from /_next/data responses. So an in-app link click
// fetches the old slug's data, gets a raw 308 to the HTML page, fails to parse
// it as route data, and falls back to a hard reload (~5s blank page).
//
// This guard intercepts the client-side transition before that data fetch
// happens and routes straight to the destination, which serves a clean 200.
// The server-side redirects stay in place as the fallback + the SEO signal.
const useBuilderRedirects = () => {
  const router = useRouter();
  const mapRef = useRef<Record<string, string> | null>(null);

  useEffect(() => {
    let active = true;

    fetch('/redirects.json')
      .then(res => (res.ok ? res.json() : null))
      .then(map => {
        if (active && map) {
          mapRef.current = map;
        }
      })
      .catch(() => {});

    const handleRouteChangeStart = (url: string) => {
      const map = mapRef.current;

      if (!map) {
        return;
      }

      const destination = map[toRedirectKey(url)];

      if (!destination || destination === url) {
        return;
      }

      // Cancel the in-flight transition, then go straight to the destination.
      router.events.emit('routeChangeError');

      if (/^https?:\/\//i.test(destination)) {
        globalThis.location.replace(destination);
      } else {
        router.replace(destination);
      }

      // Next's documented way to abort a route change from routeChangeStart.
      throw `Route to ${url} redirected to ${destination}.`;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      active = false;
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router]);
};

// Dreamdata's init snippet fires a single `page()` on the initial load, but the
// site navigates client-side, so in-app route changes never re-run init and go
// untracked (only the landing pageview is recorded per session). Fire a fresh
// pageview on each COMPLETED client transition (routeChangeComplete, not
// routeChangeStart: the path and document.title aren't updated yet at start).
// The initial load is already covered by init, so this only adds subsequent
// navigations. Both the main and cookieless (`dreamdata-cl`) queues expose
// `page`, and both are queue stubs, so the call is safe even before the CDN
// script finishes loading.
const useDreamdataPageviews = () => {
  const router = useRouter();

  useEffect(() => {
    const trackPageview = () => {
      const w = window as unknown as {
        dreamdata?: { page?: () => void };
        'dreamdata-cl'?: { page?: () => void };
      };
      w.dreamdata?.page?.();
      w['dreamdata-cl']?.page?.();
    };

    router.events.on('routeChangeComplete', trackPageview);

    return () => {
      router.events.off('routeChangeComplete', trackPageview);
    };
  }, [router]);
};

class ErrorBoundary extends ReactComponent<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

const App = ({ Component, pageProps }: AppProps) => {
  useBuilderRedirects();
  useDreamdataPageviews();

  return (
    <ErrorBoundary>
      {/* A plain div, not <main>. This wrapper exists only to carry the three
          next/font CSS variables down the tree; the real <main> landmark is
          rendered by the layouts (layout/index.tsx and layout/Landing). Using
          <main> here put a second one on every page, and a third on
          /connectors/, which is invalid HTML and leaves assistive tech with an
          ambiguous main landmark. */}
      <div className={twJoin(spaceGrotesk.variable, roboto.variable, robotoMono.variable)}>
        <Component {...pageProps} />
      </div>
    </ErrorBoundary>
  );
};

export default App;
